import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getHorseResults } from '../../../Utils/apiServices';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Grid
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const HorseDetails = () => {
  const { id } = useParams();
  const [results, setResults] = useState([]);
  const [stats, setStats] = useState([]);
  const [medals, setMedals] = useState({ gold: 0, silver: 0, bronze: 0 });

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const response = await getHorseResults(id);
        setResults(response.data.data);
        processStats(response.data.data);
        calculateMedals(response.data.data);
      } catch (error) {
        console.error('Error fetching horse results:', error);
      }
    };

    fetchResults();
  }, [id]);

  const calculateMedals = (data) => {
    const medalCount = {
      gold: 0,
      silver: 0,
      bronze: 0
    };

    data.forEach(result => {
      // Convert rank to number and check if it's a valid number
      const rank = Number(result.rank);
      if (!isNaN(rank)) { // Only process if rank is a valid number
        if (rank === 1) medalCount.gold++;
        if (rank === 2) medalCount.silver++;
        if (rank === 3) medalCount.bronze++;
      }
    });

    setMedals(medalCount);
  };

  const processStats = (data) => {
    const heightStats = {};
    
    data.forEach(result => {
      // Only process if rank is a valid number
      const rank = Number(result.rank);
      if (!isNaN(rank) && result.categoryName) {
        if (!heightStats[result.categoryName]) {
          heightStats[result.categoryName] = {
            total: 0,
            cleared: 0
          };
        }
        heightStats[result.categoryName].total += 1;
        if (result.cleared) {
          heightStats[result.categoryName].cleared += 1;
        }
      }
    });

    const processedStats = Object.entries(heightStats).map(([height, stats]) => ({
      height,
      initialRounds: stats.total,
      clearRounds: stats.cleared,
      clearRate: `${Math.round((stats.cleared / stats.total) * 100)}%`
    }));

    setStats(processedStats);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom>
            Achievements
          </Typography>
        </Grid>
        <Grid item xs={4} sx={{ textAlign: 'center' }}>
          <EmojiEventsIcon sx={{ color: '#FFD700', fontSize: 40 }} />
          <Typography variant="h6">
            Gold: {medals.gold}
          </Typography>
        </Grid>
        <Grid item xs={4} sx={{ textAlign: 'center' }}>
          <EmojiEventsIcon sx={{ color: '#C0C0C0', fontSize: 40 }} />
          <Typography variant="h6">
            Silver: {medals.silver}
          </Typography>
        </Grid>
        <Grid item xs={4} sx={{ textAlign: 'center' }}>
          <EmojiEventsIcon sx={{ color: '#CD7F32', fontSize: 40 }} />
          <Typography variant="h6">
            Bronze: {medals.bronze}
          </Typography>
        </Grid>
      </Grid>

      <Typography variant="h4" gutterBottom>
        Performance Stats
      </Typography>

      <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Height</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Initial rounds</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Clear rounds</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Clear rate</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stats.map((row, index) => (
              <TableRow 
                key={index}
                sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}
              >
                <TableCell>{row.height}</TableCell>
                <TableCell>{row.initialRounds}</TableCell>
                <TableCell>{row.clearRounds}</TableCell>
                <TableCell>{row.clearRate}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default HorseDetails;